import StatusChip from 'components/@extended/StatusChip';
import { Checkbox } from 'components/ui/checkbox';
import useAuth from 'hooks/useAuth';
import { useSetBackOfficeSettingOptionMutation } from 'store/api/back-office-settings';
import type { SettingListElement } from 'types/back-office-settings';
import { t, translations } from 'utils/getTranslations';
import type { SubOptionLayout } from './settingLayout';
import { useSettingSave } from './useSettingSave';

interface Props {
    setting: SettingListElement;
    sub: SubOptionLayout[];
    readOnly: boolean;
}

/** What an on setting draws under its description: ticks save as they change. */
export function SettingSubOptions({ setting, sub, readOnly }: Props) {
    const [setOption] = useSetBackOfficeSettingOptionMutation();
    const save = useSettingSave();
    const enabledFeatures = useAuth().user?.enabledFeatures;
    const copy = `back-office-settings.${setting.key}`;
    const ticked = (option: string) => !!setting.options.find((state) => state.key === option)?.ticked;
    const tick = (option: string, value: boolean) =>
        void save(() => setOption({ key: setting.key, option, ticked: value }).unwrap());

    return (
        <div className="mt-2 flex flex-col gap-3 max-sm:col-span-2 max-sm:min-w-0">
            {sub.map((item) => {
                switch (item.kind) {
                    case 'ticks':
                        return (
                            <div key="ticks" className="flex flex-col gap-2.5">
                                {item.ticks
                                    .filter(({ paidFlag }) => !paidFlag || enabledFeatures?.includes(paidFlag))
                                    .map(({ option, paidFlag }) => {
                                        const hint = `${copy}.hints.${option}`;
                                        return (
                                            <label
                                                key={option}
                                                className="flex cursor-pointer items-start gap-2 text-[13px] leading-[19px]"
                                            >
                                                <Checkbox
                                                    className="mt-0.5"
                                                    checked={ticked(option)}
                                                    disabled={readOnly}
                                                    onCheckedChange={(value) => tick(option, value)}
                                                />
                                                <span className="flex flex-col items-start gap-0.5">
                                                    <span className="text-fg font-medium">
                                                        {t(`${copy}.options.${option}`)}
                                                    </span>
                                                    {hint in translations ? (
                                                        <span className="text-grey-600 max-w-[620px] text-xs leading-[17px]">
                                                            {t(hint)}
                                                        </span>
                                                    ) : null}
                                                    {paidFlag ? (
                                                        <StatusChip
                                                            size="compact"
                                                            className="mt-0.5 h-5 rounded-[6px] leading-5"
                                                            label={t('back-office-settings.only-while-module', {
                                                                module: t(`back-office-settings.modules.${paidFlag}`),
                                                            })}
                                                        />
                                                    ) : null}
                                                </span>
                                            </label>
                                        );
                                    })}
                            </div>
                        );
                    case 'value':
                        return (
                            <div key="value" className="flex flex-wrap items-end gap-3">
                                <item.Content />
                            </div>
                        );
                    case 'list':
                        return (
                            <div key="list" className="flex max-w-[760px] flex-col gap-2">
                                <item.Content />
                            </div>
                        );
                }
            })}
        </div>
    );
}
